<script lang="ts">
  // Group · Leaderboard: the group session's ranking (by how well each rider matched the
  // session), with the sender highlighted, and the session's award winners.
  import { MEDAL_COLORS, type ReportContext } from '../context'
  import Avatar from '../parts/Avatar.svelte'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import Frame from '../parts/Frame.svelte'
  import Icon from '../parts/Icon.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  $: s = ctx.data.group!.session!
  const medalByNum: Record<number, keyof typeof MEDAL_COLORS> = { 3: 'gold', 2: 'silver', 1: 'bronze' }
</script>

<Frame {ctx} base="radial-gradient(120% 70% at 0% 0%, #2a2350 0%, #120f26 55%, #08070f 100%)">
  <div class="xr-layout">
    <div class="xr-top">
      <span class="xr-eyebrow"><Icon name="trophy" size={30} stroke={2.4} color="var(--accent)" /> Group Session</span>
      <Brand height={38} />
    </div>

    <div class="xr-head">
      <h2 class="xr-session">{s.name}</h2>
      {#if ctx.size !== 'square' && s.workout_name && s.workout_name !== s.name}
        <div class="xr-workout">{s.workout_name}</div>
      {/if}
    </div>

    {#if ctx.show('rank') && s.rank}
      <div class="xr-placed">
        <span class="xr-placed-num">{ctx.fmt.ordinal(s.rank)}</span>
        <span class="xr-placed-of">of {s.participants} riders</span>
      </div>
    {/if}

    {#if ctx.show('leaderboard')}
      <div class="xr-board">
        <div class="xr-board-head">
          <span>Rider</span><span>XSS</span><span>Difficulty</span>
        </div>
        {#each s.leaderboard as r}
          <div class="xr-board-row" class:xr-me={r.is_me}>
            <span class="xr-rank">{r.rank}</span>
            <Avatar src={r.is_me ? ctx.data.athlete.avatar : r.avatar} name={r.is_me ? ctx.data.athlete.name : r.name} blurred={r.blurred} size={ctx.size === 'square' ? 50 : 64} ringWidth={0} />
            <span class="xr-who">
              {r.is_me ? 'You' : r.blurred ? 'Xert athlete' : r.name}
              {#if r.medal && medalByNum[r.medal]}<Icon name="award" size={26} stroke={2.6} color={MEDAL_COLORS[medalByNum[r.medal]].main} />{/if}
            </span>
            <span class="xr-num">{r.xss}</span>
            <span class="xr-num">{r.difficulty}</span>
          </div>
        {/each}
      </div>
    {/if}

    {#if ctx.show('awards')}
      <div class="xr-awards">
        <div class="xr-award"><span class="xr-k">Most XSS</span><span class="xr-v">{s.awards.xss}</span></div>
        <div class="xr-award"><span class="xr-k">Hardest Effort</span><span class="xr-v">{s.awards.difficulty}</span></div>
        <div class="xr-award"><span class="xr-k">Best W/kg</span><span class="xr-v">{s.awards.wkg}</span></div>
      </div>
    {/if}

    <div class="xr-title"><TitleBlock {ctx} lines={1} /></div>
    <div class="xr-badges"><Badges {ctx} own="group" max={ctx.size === 'square' ? 2 : Infinity} /></div>
  </div>
</Frame>

<style>
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto auto auto minmax(0, 1fr) auto auto auto;
    grid-template-areas: 'top' 'head' 'placed' 'board' '.' 'awards' 'title' 'badges';
    row-gap: 24px;
  }
  :global(.xr-square) .xr-layout { row-gap: 16px; }
  /* Landscape: the session, your placing and the awards on the left; the leaderboard on the right. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: 560px minmax(0, 1fr);
    grid-template-rows: auto auto auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top top'
      'head board'
      'placed board'
      'awards board'
      'title board'
      'badges badges';
    column-gap: 70px;
    row-gap: 28px;
  }
  .xr-top { grid-area: top; display: flex; align-items: center; justify-content: space-between; }
  .xr-head { grid-area: head; min-width: 0; }
  .xr-placed { grid-area: placed; }
  .xr-board { grid-area: board; }
  :global(.xr-landscape) .xr-board { align-self: center; }
  .xr-awards { grid-area: awards; }
  .xr-title { grid-area: title; min-width: 0; }
  :global(.xr-landscape) .xr-title { align-self: end; }
  .xr-badges { grid-area: badges; }
  .xr-badges:empty { display: none; }
  :global(.xr-square) .xr-session { font-size: 72px; }
  :global(.xr-square) .xr-placed-num { font-size: 104px; }
  :global(.xr-square) .xr-placed-of { font-size: 44px; }
  :global(.xr-square) .xr-board { gap: 8px; }
  :global(.xr-square) .xr-board-row { padding: 4px 20px; grid-template-columns: 48px 50px 1fr 120px 150px; }
  :global(.xr-square) .xr-board-head { padding-left: 138px; grid-template-columns: 1fr 120px 150px; }
  :global(.xr-square) .xr-rank, :global(.xr-square) .xr-num { font-size: 38px; }
  :global(.xr-square) .xr-who { font-size: 26px; }
  :global(.xr-square) .xr-award { padding: 12px 20px; }
  :global(.xr-landscape) .xr-session { white-space: normal; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; font-size: 80px; }
  /* Landscape: the awards stack in the narrow left column. */
  :global(.xr-landscape) .xr-awards { grid-template-columns: minmax(0, 1fr); gap: 12px; }
  :global(.xr-landscape) .xr-award { flex-direction: row; justify-content: space-between; align-items: baseline; gap: 20px; padding: 14px 22px; }
  .xr-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 0.26em;
    text-transform: uppercase;
  }
  .xr-session {
    font-family: var(--display);
    font-weight: 800;
    font-size: 84px;
    line-height: 1;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .xr-workout {
    margin-top: 10px;
    font-size: 30px;
    color: var(--muted);
  }
  .xr-placed {
    display: flex;
    align-items: baseline;
    gap: 20px;
  }
  .xr-placed-num {
    font-family: var(--display);
    font-weight: 800;
    font-style: italic;
    font-size: 140px;
    line-height: 0.85;
    color: var(--accent);
  }
  .xr-placed-of {
    font-family: var(--display);
    font-size: 54px;
    font-weight: 700;
    text-transform: uppercase;
  }
  .xr-board {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .xr-board-head, .xr-board-row {
    display: grid;
    grid-template-columns: 56px 64px 1fr 130px 160px;
    align-items: center;
    column-gap: 18px;
  }
  .xr-board-head {
    grid-template-columns: 1fr 130px 160px;
    padding: 0 22px 0 160px;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--muted);
  }
  .xr-board-head span:not(:first-child), .xr-num {
    text-align: right;
  }
  .xr-board-row {
    padding: 8px 22px;
    border-radius: 18px;
    background: var(--panel);
  }
  .xr-board-row.xr-me {
    background: rgba(var(--accent-rgb), 0.22);
    box-shadow: inset 0 0 0 3px var(--accent);
  }
  .xr-rank {
    font-family: var(--display);
    font-size: 46px;
    font-weight: 800;
    text-align: center;
  }
  .xr-who {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    font-size: 30px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .xr-num {
    font-family: var(--display);
    font-size: 46px;
    font-weight: 700;
  }
  .xr-awards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
  .xr-award {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 18px 22px;
    border-radius: 18px;
    border: 2px solid var(--faint);
    min-width: 0;
  }
  .xr-k {
    font-size: 19px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
  }
  .xr-v {
    font-size: 28px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
